// Central content/config for the Services overview page.
// Division identities come from src/data/site.js (SERVICES); directory links
// resolve against the Ecosystem catalogue (src/data/ecosystem.js) so service
// names/routes are never duplicated. The future admin area will manage this
// content without code changes.

export const SERVICES_SEO = {
  title: 'Business Consultancy & Support Services | Amden Group',
  description:
    'Explore Amden Group services across operations, HR and people, marketing and growth, business systems, recruitment and new business launch support.',
  path: '/services',
};

export const SERVICES_HERO = {
  eyebrow: 'Our Services',
  heading: 'Practical support for every stage of your business',
  copy: 'Amden combines consultancy, hands-on implementation, business systems and specialist services to help organisations improve operations, strengthen people, grow and launch successfully.',
  supportingLine: 'One business partner. Four connected service areas.',
  primaryLabel: 'Explore Our Services',
  primaryTarget: '#service-areas',
  secondaryLabel: 'Tell Us What You Need',
  secondaryTarget: '#by-challenge',
  image: {
    src: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',
    alt: 'A management team reviewing different areas of their business with an experienced adviser',
    width: 1200,
    height: 800,
  },
};

// Four connected service areas. Accent colours come from the division itself.
export const SERVICE_AREAS = [
  {
    division: 'operations',
    heading: 'Improve how the business runs',
    description:
      'Strengthen processes, accountability, reporting, systems and day-to-day operational performance.',
    services: [
      'Operational reviews',
      'Process improvement',
      'Reporting and KPIs',
      'Workflow and systems implementation',
      'CRM and automation',
      'Ongoing operational support',
    ],
  },
  {
    division: 'people',
    heading: 'Build stronger teams',
    description:
      'Recruit, manage and develop your people through practical HR, recruitment, training and leadership support.',
    services: [
      'HR consultancy',
      'Recruitment support',
      'HR packs and documentation',
      'Team development',
      'Leadership and management training',
      'Everything DiSC® profiles',
    ],
  },
  {
    division: 'growth',
    heading: 'Create sustainable commercial growth',
    description: 'Improve your positioning, marketing, website, content and commercial strategy.',
    services: [
      'Social media support',
      'Marketing advice',
      'Website support',
      'Strategy building',
      'Marketing training',
      'Lead-generation support',
    ],
  },
  {
    division: 'launch',
    heading: 'Turn an idea into a working business',
    description:
      'Bring planning, structure, branding, websites, systems and practical launch support together.',
    services: [
      'Business idea review',
      'Business planning',
      'Launch strategy',
      'Brand and website',
      'Systems setup',
      'Full launch support',
    ],
  },
];

// Business-challenge → recommended route mappings (central, data-driven).
// divisions: recommended Amden areas. exploreHref: where "Explore This
// Service" leads. The enquiry action carries the challenge + source.
export const SERVICE_CHALLENGES = [
  {
    id: 'inefficient-processes',
    label: 'Our Processes Are Inefficient',
    divisions: ['operations'],
    explanation:
      'Inefficiency usually comes from unclear processes, duplicated effort or missing accountability — an operational review finds where time and margin are lost.',
    services: ['Operational Review', 'Process-Mapping Workshop', 'Workflow Automation Review'],
    exploreHref: '/services/operations',
  },
  {
    id: 'visibility-accountability',
    label: 'We Lack Visibility and Accountability',
    divisions: ['operations'],
    explanation:
      'When reporting, KPIs and management rhythms are weak, problems surface late. Structured reporting and clear ownership change that.',
    services: ['KPI and Reporting Review', 'Management Structure Review', 'Amden Compass'],
    exploreHref: '/services/operations',
  },
  {
    id: 'better-systems-crm',
    label: 'We Need Better Systems or a CRM',
    divisions: ['operations', 'ecosystem'],
    explanation:
      'The right system depends on how your business actually works. Amden assesses, implements and supports practical systems — including its own platforms.',
    services: ['Systems Assessment', 'CRM Setup', 'Amden Reach'],
    exploreHref: '/ecosystem',
  },
  {
    id: 'struggling-to-recruit',
    label: 'We Are Struggling to Recruit',
    divisions: ['people'],
    explanation:
      'Recruitment problems usually start before the job ad — in how the role is defined and positioned. Structured support improves both attraction and selection.',
    services: ['Recruitment Retainer', 'Pay per Placement', 'Business Planning Session'],
    exploreHref: '/services/people',
  },
  {
    id: 'hr-structures',
    label: 'We Need Stronger HR Structures',
    divisions: ['people'],
    explanation:
      'Clear documentation, policies and processes protect the business and support your team as it grows.',
    services: ['HR Packs', 'HR Consultancy', 'Onboarding Support'],
    exploreHref: '/services/people',
  },
  {
    id: 'team-development',
    label: 'Our Managers or Team Need Development',
    divisions: ['people'],
    explanation:
      'Practical training and profiling tools build communication, accountability and management capability that lasts.',
    services: ['Leadership and Management Training', 'Team Training', 'Everything DiSC® Profiles'],
    exploreHref: '/services/people',
  },
  {
    id: 'inconsistent-marketing',
    label: 'Our Marketing Is Inconsistent',
    divisions: ['growth'],
    explanation:
      'Inconsistency usually means missing structure: no plan, no rhythm, no measurement. A clear strategy and support model fixes the foundation first.',
    services: ['Marketing Review', 'Marketing Strategy Workshop', 'Ongoing Social Media Support'],
    exploreHref: '/services/growth',
  },
  {
    id: 'website-not-performing',
    label: 'Our Website Is Not Performing',
    divisions: ['growth'],
    explanation:
      'A website should build credibility and generate enquiries. An audit shows exactly where yours is losing visitors and opportunities.',
    services: ['Website Audit', 'Website Support', 'Website Development'],
    exploreHref: '/services/growth',
  },
  {
    id: 'clearer-growth-strategy',
    label: 'We Need a Clearer Growth Strategy',
    divisions: ['growth'],
    explanation:
      'Sustainable growth comes from clear positioning, the right channels and measurable priorities — not more scattered activity.',
    services: ['Marketing Strategy Workshop', 'Marketing Review', 'Marketing Training'],
    exploreHref: '/services/growth',
  },
  {
    id: 'start-a-business',
    label: 'I Want to Start a Business',
    divisions: ['launch'],
    explanation:
      'A structured start beats a fast one. Amden Launch helps you test the idea, plan properly and build the right foundations from day one.',
    services: ['Business Idea Review', 'Business Planning Session', 'Full Business Launch Programme'],
    exploreHref: '/services/launch',
  },
  {
    id: 'launching-something-new',
    label: 'We Are Launching Something New',
    divisions: ['launch'],
    explanation:
      'New ventures and offerings need positioning, brand, systems and a practical route to market — brought together, not bolted on.',
    services: ['Launch Strategy', 'Brand Starter Package', 'Business Website Package'],
    exploreHref: '/services/launch',
  },
  {
    id: 'specific-product-partner',
    label: 'I Need a Specific Product or Partner',
    divisions: ['ecosystem'],
    explanation:
      'The Amden Ecosystem brings systems, defined service packages, technology platforms and trusted specialist partners together in one catalogue.',
    services: ['Amden Systems', 'Fixed-Scope Services', 'Partner Solutions'],
    exploreHref: '/ecosystem',
  },
];

// Services at a glance. Items with `slug` resolve name + route from the
// Ecosystem catalogue (single source of truth); items with `href` link to
// the relevant division page until dedicated detail pages exist.
export const SERVICE_DIRECTORY = [
  {
    division: 'operations',
    items: [
      { slug: 'operational-review' },
      { slug: 'process-mapping-workshop' },
      { slug: 'workflow-automation-review' },
      { slug: 'kpi-reporting-review' },
      { slug: 'management-structure-review' },
      { slug: 'systems-assessment' },
      { slug: 'crm-setup' },
      { name: 'Automation', href: '/services/operations' },
      { slug: 'amden-compass' },
      { slug: 'amden-pulse' },
      { slug: 'amden-foresee' },
      { slug: 'amden-reach' },
    ],
  },
  {
    division: 'people',
    items: [
      { name: 'HR Consultancy', href: '/services/people' },
      { name: 'Ongoing HR Support', href: '/services/people' },
      { slug: 'recruitment-retainer' },
      { slug: 'pay-per-placement' },
      { slug: 'hr-packs' },
      { slug: 'everything-disc-profiles' },
      { slug: 'team-training' },
      { slug: 'leadership-management-training' },
      { name: 'Onboarding Support', href: '/services/people' },
      { name: 'People and Organisational Development', href: '/services/people' },
    ],
  },
  {
    division: 'growth',
    items: [
      { slug: 'ongoing-social-media-support' },
      { slug: 'marketing-review' },
      { slug: 'website-support' },
      { slug: 'website-audit' },
      { slug: 'marketing-strategy-workshop' },
      { slug: 'social-media-planning-session' },
      { slug: 'marketing-training' },
      { slug: 'website-development' },
      { name: 'Lead-Generation Support', href: '/services/growth' },
    ],
  },
  {
    division: 'launch',
    items: [
      { slug: 'business-idea-review' },
      { slug: 'business-planning-session' },
      { slug: 'launch-strategy' },
      { slug: 'brand-starter-package' },
      { slug: 'business-website-package' },
      { slug: 'systems-starter-package' },
      { slug: 'full-business-launch-programme' },
      { slug: 'post-launch-support' },
    ],
  },
];

export const SERVICES_ECO_PROMO = {
  eyebrow: 'Amden Ecosystem',
  heading: 'Systems, products and specialist solutions',
  copy: 'Explore Amden systems, defined service packages, technology platforms, affiliate products and trusted specialist partners.',
  highlights: [
    'Amden Compass',
    'Amden Pulse',
    'Amden Foresee',
    'Amden Reach',
    'Fixed-scope services & packages',
    'Technology & CRM partners',
    'Affiliate products',
    'Professional partner services',
  ],
  primaryLabel: 'Explore the Amden Ecosystem',
  primaryHref: '/ecosystem',
  secondaryLabel: 'Help Me Choose',
  secondaryHref: '/?source=Services%20Overview%20%E2%80%94%20Ecosystem#solution-finder',
};

export const HOW_AMDEN_WORKS = {
  eyebrow: 'Our Approach',
  heading: 'Advice that guides. Solutions that deliver.',
  statement:
    'Amden can provide a focused individual service or support a wider programme across several parts of the business.',
  stages: [
    {
      step: '01',
      title: 'Understand',
      copy: 'We take time to understand the business, the people involved and the real issue that needs to be solved.',
    },
    {
      step: '02',
      title: 'Recommend',
      copy: 'We define the priorities, practical solution and expected outcome.',
    },
    {
      step: '03',
      title: 'Implement',
      copy: 'We work alongside the business to introduce the agreed changes, services or systems.',
    },
    {
      step: '04',
      title: 'Improve',
      copy: 'We review progress, measure performance and continue strengthening the result.',
    },
  ],
};

export const NOT_SURE = {
  heading: 'Not sure which service you need?',
  copy: 'Tell us about your business, its current stage and the challenge you are facing. We’ll guide you towards the most relevant support.',
  actionLabel: 'Start the Guided Finder',
  // Links to the homepage's approved guided solution finder, preserving the
  // visitor's journey and enquiry source. The same shared component, data
  // model and backend service — nothing duplicated.
  actionHref: '/?source=Services%20Overview%20Solution%20Finder#solution-finder',
};
